import mimeDb from 'mime-db/db.json'

export function extension(mime: string): string | undefined {
  const result = (
    mimeDb as Record<string, { extensions?: string[] } | undefined>
  )[mime]
  return result?.extensions?.[0]
}

// Lazily-built reverse index of file extension -> mime type, from mime-db.
// First mime registered for an extension wins, which matches mime-db's own
// preference order.
let extensionToMime: Record<string, string> | undefined

function reverseIndex(): Record<string, string> {
  if (!extensionToMime) {
    extensionToMime = {}
    const db = mimeDb as Record<string, { extensions?: string[] } | undefined>
    for (const [mime, meta] of Object.entries(db)) {
      for (const ext of meta?.extensions ?? []) {
        if (!(ext in extensionToMime)) {
          extensionToMime[ext] = mime
        }
      }
    }
  }
  return extensionToMime
}

// mimeFromFilename resolves a file's mime type from its extension. Browsers
// frequently report an empty or inconsistent File.type (notably for .md, .csv,
// and .txt), so the filename extension is a more reliable signal for routing
// and for labeling downloads.
export function mimeFromFilename(filename: string): string | undefined {
  const ext = filename.split('.').pop()?.toLowerCase()
  return ext ? reverseIndex()[ext] : undefined
}

// extensionsForMime returns the dotted file extensions registered for a mime
// (e.g. 'text/markdown' -> ['.md', '.markdown']), for use as a react-dropzone
// accept value so files are matched by extension as well as by File.type.
export function extensionsForMime(mime: string): string[] {
  const db = mimeDb as Record<string, { extensions?: string[] } | undefined>
  return (db[mime]?.extensions ?? []).map((ext) => `.${ext}`)
}

// resolveUploadMime picks the mime to send for an uploaded file. The filename
// extension is preferred over the browser's File.type, which is unreliable --
// browsers mislabel a CSV as application/vnd.ms-excel or report
// application/octet-stream for a file whose extension resolves cleanly.
// Returns '' when neither is known, leaving the server to decide.
export function resolveUploadMime(file: File): string {
  return mimeFromFilename(file.name) || file.type || ''
}
